import { useCallback, useEffect, useRef, useState } from 'react';
import { playRhythmHit } from '../audio/rhythmSounds';
import { resumeAudio } from '../audio/noteSounds';
import type { RhythmPattern } from '../utils/rhythmPattern';
import { rhythmToMelodyPattern } from '../utils/rhythmPlayback';
import { flattenPlayback } from '../utils/pattern';

export function useRhythmPlayback(
  pattern: RhythmPattern,
  bpm: number,
  soundEnabled: boolean,
) {
  const [playing, setPlaying] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeMeasureIndex, setActiveMeasureIndex] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const indexRef = useRef(0);

  const stop = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setPlaying(false);
    setActiveIndex(null);
    setActiveMeasureIndex(null);
    indexRef.current = 0;
  }, []);

  const play = useCallback(async () => {
    stop();
    const melody = rhythmToMelodyPattern(pattern);
    const events = flattenPlayback(melody, bpm);
    if (events.length === 0) return;

    if (soundEnabled) await resumeAudio();

    let cellIdx = 0;
    const flatCells = pattern.measures.flatMap((m) =>
      m.slots.flatMap((s) => (s.active ? s.cells : [])),
    );

    setPlaying(true);
    indexRef.current = 0;

    const tick = () => {
      const i = indexRef.current;
      if (i >= events.length) {
        stop();
        return;
      }
      const { globalIndex, measureIndex, durationMs } = events[i];
      const rhythmCell = flatCells[cellIdx];
      setActiveIndex(globalIndex);
      setActiveMeasureIndex(measureIndex);
      if (soundEnabled && rhythmCell?.hit) {
        playRhythmHit(durationMs, rhythmCell.figure === 'negra' || rhythmCell.figure === 'corchea');
      }
      cellIdx++;
      indexRef.current = i + 1;
      timerRef.current = setTimeout(tick, durationMs);
    };

    tick();
  }, [pattern, bpm, soundEnabled, stop]);

  useEffect(() => () => stop(), [stop]);

  return { playing, activeIndex, activeMeasureIndex, play, stop };
}
